import { useDebounce } from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";
import { Loader, Search, X } from "lucide-react";
import { ChangeEvent, memo, useEffect, useRef, useState } from "react";
import { Input } from "./input";

interface SearchInputProps {
  onSearch: (searchTerm: string) => void;
  delay?: number;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  isLoading?: boolean;
}

const leftIcon = {
  search: Search,
  loading: Loader,
};

function SearchInputComponent({
  delay = 300,
  placeholder = "Search...",
  className = "",
  inputClassName = "",
  onSearch,
  isLoading = false,
}: SearchInputProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, delay);
  const prevousSearchTerm = useRef("");

  useEffect(() => {
    if (prevousSearchTerm.current !== debouncedSearchTerm) {
      onSearch(debouncedSearchTerm);
      prevousSearchTerm.current = debouncedSearchTerm;
    }
  }, [debouncedSearchTerm, onSearch]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const LeftIcon = leftIcon[isLoading ? "loading" : "search"];

  return (
    <div
      className={cn(
        "relative max-w-[300px] w-full sm:w-[300px] bg-background",
        className
      )}
    >
      <div className="absolute top-0 left-3 bottom-0 flex items-center justify-center">
        {LeftIcon && (
          <LeftIcon
            className={cn("text-gray-400 w-4 h-4", isLoading && "animate-spin")}
          />
        )}
      </div>

      <Input
        placeholder={placeholder}
        className={cn("pl-9 w-full text-sm ", inputClassName)}
        value={searchTerm}
        onChange={handleChange}
      />

      {searchTerm && (
        <button
          className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 p-1"
          onClick={() => setSearchTerm("")}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export const SearchInput = memo(SearchInputComponent);
